import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Target, AlertTriangle, CheckCircle } from 'lucide-react';
import { config } from '@/config/config';
import {
  fetchAttributeAdoption,
  type AttributeAdoptionResponse,
  type AttributeAdoption,
} from '@/api/attributeInsights';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { translateAttributeType } from '@/utils/attributeTypeTranslations';
import { useTheme } from '@/contexts/ThemeContext';

// Chart colors from config
const COLORS = config.reports.display.chartColors;
const TREND_COLORS = {
  up: '#10b981',
  down: '#ef4444',
  stable: '#6b7280',
};

const AttributeInsightsReport: React.FC = () => {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { t } = useTranslation('translation');
  const { actualTheme } = useTheme();

  // State for filters - using default values without UI controls
  const [adoptionFilters] = useState({
    top: 10,
    trend: undefined as string | undefined,
    data_type: undefined as string | undefined,
  });

  // Fetch attribute adoption data
  const {
    data: adoptionData,
    isLoading: adoptionLoading,
    error: adoptionError,
  } = useQuery<AttributeAdoptionResponse>({
    queryKey: ['attribute-adoption', adoptionFilters],
    queryFn: async () => {
      return await fetchAttributeAdoption(adoptionFilters);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Process adoption data for charts
  const adoptionChartData = useMemo(() => {
    if (!adoptionData?.results) return [];

    return adoptionData.results.map(attr => ({
      name:
        attr.attribute_label.length > 20
          ? `${attr.attribute_label.substring(0, 17)}...`
          : attr.attribute_label,
      fullName: attr.attribute_label,
      adoption: Number(attr.adoption_pct),
      filled: attr.filled_count,
      total: attr.total_count,
      trend: attr.trend_direction,
      trendChange: Number(attr.trend_change_pct),
      dataType: attr.attribute_data_type,
      uniqueValues: attr.unique_values_count,
    }));
  }, [adoptionData]);

  // Calculate summary statistics
  const adoptionSummary = useMemo(() => {
    if (!adoptionData?.results) return null;

    const results = adoptionData.results;
    const avgAdoption =
      results.reduce((sum, attr) => sum + Number(attr.adoption_pct), 0) / results.length;
    const trendCounts = results.reduce(
      (acc, attr) => {
        acc[attr.trend_direction]++;
        return acc;
      },
      { up: 0, down: 0, stable: 0 }
    );

    return {
      avgAdoption: Number(avgAdoption.toFixed(1)),
      totalAttributes: adoptionData.total_attributes,
      trendCounts,
      topAttribute: results[0],
      bottomAttribute: results[results.length - 1],
    };
  }, [adoptionData]);

  const TrendIcon = ({ trend, change }: { trend: string; change: number }) => {
    if (trend === 'up') return <TrendingUp className="size-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="size-4 text-red-600" />;
    return <Minus className="size-4 text-gray-600" />;
  };

  // Theme-aware label color for charts
  const labelColor = actualTheme === 'dark' ? '#e5e7eb' : '#6b7280';

  if (adoptionLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (adoptionError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <AlertTriangle className="mx-auto mb-4 size-12" />
            <h3 className="mb-2 text-lg font-semibold">
              {i18n.getAttributesText('errors.analyticsUnavailable')}
            </h3>
            <p className={`text-sm ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {i18n.getAttributesText('errors.analyticsRefreshing')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2
            className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-foreground' : 'text-enterprise-900'}`}
          >
            {i18n.getAttributesText('title')}
          </h2>
          <p
            className={`mt-1 ${actualTheme === 'dark' ? 'text-muted-foreground' : 'text-enterprise-600'}`}
          >
            {i18n.getAttributesText('subtitle')}
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          {i18n.attributesBadges.lastUpdated}{' '}
          {adoptionData?.snapshot_date || i18n.attributesBadges.unknown}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {i18n.attributesSummaryCards.avgAdoption}
                </p>
                <p
                  className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-foreground' : 'text-enterprise-900'}`}
                >
                  {adoptionSummary?.avgAdoption || 0}%
                </p>
              </div>
              <Target className="size-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {i18n.attributesSummaryCards.totalAttributes}
                </p>
                <p
                  className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-foreground' : 'text-enterprise-900'}`}
                >
                  {adoptionSummary?.totalAttributes || 0}
                </p>
              </div>
              <CheckCircle className="size-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {i18n.attributesSummaryCards.trendingUp}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {adoptionSummary?.trendCounts.up || 0}
                </p>
              </div>
              <TrendingUp className="size-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attribute Adoption Content */}
      <div className="space-y-4">
        {/* Adoption Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {i18n.attributesCharts.attributeAdoptionRates}
            </CardTitle>
            <CardDescription>{i18n.attributesCharts.adoptionRatesDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              {adoptionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={adoptionChartData}
                    layout="vertical"
                    margin={{ top: 5, right: 50, left: 120, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      unit="%"
                      tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={100}
                      tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${Number(value).toFixed(1)}%`,
                        i18n.attributesCharts.adoptionRate,
                      ]}
                      labelFormatter={name => {
                        const item = adoptionChartData.find(i => i.name === name);
                        return `${item?.fullName} (${item?.filled} of ${item?.total})`;
                      }}
                      contentStyle={{
                        backgroundColor: actualTheme === 'dark' ? '#1f2937' : '#ffffff',
                        border: actualTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
                        borderRadius: '6px',
                        color: actualTheme === 'dark' ? '#f9fafb' : '#111827',
                        boxShadow:
                          actualTheme === 'dark'
                            ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)'
                            : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      }}
                      labelStyle={{
                        color: actualTheme === 'dark' ? '#f9fafb' : '#111827',
                        fontWeight: '500',
                      }}
                      itemStyle={{
                        color: actualTheme === 'dark' ? '#e5e7eb' : '#374151',
                      }}
                    />
                    <Bar dataKey="adoption" radius={[0, 4, 4, 0]}>
                      {adoptionChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={TREND_COLORS[entry.trend as keyof typeof TREND_COLORS]}
                        />
                      ))}
                      <LabelList
                        dataKey="adoption"
                        position="right"
                        formatter={(value: number) => `${value.toFixed(1)}%`}
                        style={{ fill: labelColor, fontSize: 12 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div
                  className={`flex h-full items-center justify-center ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {i18n.errors.attributes.noDataAvailable}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Adoption Details Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{i18n.attributesCharts.adoptionDetails}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th
                      className={`p-2 text-left ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.attribute}
                    </th>
                    <th
                      className={`p-2 text-left ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.type}
                    </th>
                    <th
                      className={`p-2 text-right ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.adoption}
                    </th>
                    <th
                      className={`p-2 text-right ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.filled}
                    </th>
                    <th
                      className={`p-2 text-center ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.trend}
                    </th>
                    <th
                      className={`p-2 text-right ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                    >
                      {i18n.attributesTable.headers.uniqueValues}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adoptionChartData.map((attr, index) => (
                    <tr
                      key={index}
                      className={`border-b ${actualTheme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                    >
                      <td
                        className={`p-2 font-medium ${actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
                      >
                        {attr.fullName}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className="text-xs">
                          {translateAttributeType(attr.dataType, t)}
                        </Badge>
                      </td>
                      <td
                        className={`p-2 text-right font-semibold ${actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
                      >
                        {attr.adoption.toFixed(1)}%
                      </td>
                      <td
                        className={`p-2 text-right ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                      >
                        {attr.filled.toLocaleString()} / {attr.total.toLocaleString()}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TrendIcon trend={attr.trend} change={attr.trendChange} />
                          {attr.trendChange !== 0 && (
                            <span
                              className={`text-xs ${
                                attr.trend === 'up'
                                  ? 'text-green-600'
                                  : attr.trend === 'down'
                                    ? 'text-red-600'
                                    : 'text-gray-600'
                              }`}
                            >
                              {attr.trendChange > 0 ? '+' : ''}
                              {attr.trendChange.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className={`p-2 text-right ${actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                      >
                        {attr.uniqueValues.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AttributeInsightsReport;
